// src/services/jobService.js
// 求人関連のFirestore操作

import { collection, doc, addDoc, updateDoc, deleteDoc, getDoc, query, where, orderBy, limit, onSnapshot, serverTimestamp, getDocs, startAfter } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../config/firebase';

// ══════════════════════════════
// 求人 CRUD
// ══════════════════════════════

// ── 求人作成 ──
export async function createJob(data) {
  const jobRef = await addDoc(collection(db, 'jobs'), {
    shopName: data.shopName,
    area: data.area,
    genre: data.genre || '',
    salaryMin: data.salaryMin || '',
    salaryMax: data.salaryMax || '',
    salaryDisplay: data.salaryDisplay || '',
    tags: data.tags || [],
    photoUrl: data.photoUrl || '',
    description: data.description || '',
    active: data.active !== false,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp() });
  return jobRef.id;
}

// ── 求人更新 ──
export async function updateJob(jobId, data) {
  await updateDoc(doc(db, 'jobs', jobId), {
    ...data,
    updatedAt: serverTimestamp() });
}

// ── 求人削除 ──
export async function deleteJob(jobId) {
  await deleteDoc(doc(db, 'jobs', jobId));
}

// ── 公開/非公開切り替え ──
export async function toggleJobActive(jobId, active) {
  await updateDoc(doc(db, 'jobs', jobId), {
    active,
    updatedAt: serverTimestamp() });
}

// ── 求人写真アップロード ──
export async function uploadJobPhoto(jobId, file) {
  const storageRef = ref(storage, `jobs/${jobId}/photo_${Date.now()}`);
  const snapshot = await uploadBytes(storageRef, file);
  const url = await getDownloadURL(snapshot.ref);
  await updateDoc(doc(db, 'jobs', jobId), {
    photoUrl: url,
    updatedAt: serverTimestamp() });
  return url;
}

// ── 求人一覧（リアルタイム） ──
export function subscribeJobs(callback, activeOnly = false) {
  let q;
  if (activeOnly) {
    q = query(collection(db, 'jobs'), where('active', '==', true), orderBy('createdAt', 'desc'), limit(200));
  } else {
    q = query(collection(db, 'jobs'), orderBy('createdAt', 'desc'), limit(200));
  }
  return onSnapshot(q, (snapshot) => {
    const jobs = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    callback(jobs);
  });
}

// ── 求人1件取得 ──
export async function getJob(jobId) {
  const snap = await getDoc(doc(db, 'jobs', jobId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

// ══════════════════════════════
// 求人紹介
// ══════════════════════════════

// ── 求人紹介を作成（スタッフ → ユーザー） ──
export async function createJobIntroduction(data) {
  const introRef = await addDoc(collection(db, 'jobIntroductions'), {
    userId: data.userId,
    jobId: data.jobId,
    staffId: data.staffId,
    staffComment: data.staffComment || '',
    matchPercent: data.matchPercent || 0,
    status: 'sent',
    sentAt: serverTimestamp() });
  return introRef.id;
}

// ── ユーザーの紹介一覧 ──
export function subscribeUserIntroductions(userId, callback) {
  const q = query(
    collection(db, 'jobIntroductions'),
    where('userId', '==', userId),
    orderBy('sentAt', 'desc')
  );
  return onSnapshot(q, (snapshot) => {
    const intros = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    callback(intros);
  });
}

// ── 紹介ステータス更新 ──
export async function updateIntroductionStatus(introId, status) {
  await updateDoc(doc(db, 'jobIntroductions', introId), { status });
}


// ==============================
// Paging helpers for Admin lists
// ==============================

export function subscribeLatestJobs(callback, pageSize = 50, activeOnly = false) {
  const onlyActive = !!activeOnly;

  let q;
  if (onlyActive) {
    q = query(
      collection(db, 'jobs'),
      where('active', '==', true),
      orderBy('createdAt', 'desc'),
      limit(pageSize)
    );
  } else {
    q = query(
      collection(db, 'jobs'),
      orderBy('createdAt', 'desc'),
      limit(pageSize)
    );
  }

  return onSnapshot(q, (snapshot) => {
    const items = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    const lastDoc = snapshot.docs[snapshot.docs.length - 1] || null;
    callback({ items, lastDoc });
  });
}

export async function fetchMoreJobs(lastDoc, pageSize = 50, activeOnly = false) {
  if (!lastDoc) return { items: [], lastDoc: null };
  const onlyActive = !!activeOnly;

  let q;
  if (onlyActive) {
    q = query(
      collection(db, 'jobs'),
      where('active', '==', true),
      orderBy('createdAt', 'desc'),
      startAfter(lastDoc),
      limit(pageSize)
    );
  } else {
    q = query(
      collection(db, 'jobs'),
      orderBy('createdAt', 'desc'),
      startAfter(lastDoc),
      limit(pageSize)
    );
  }

  const snapshot = await getDocs(q);
  const items = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  const nextLastDoc = snapshot.docs[snapshot.docs.length - 1] || null;
  return { items, lastDoc: nextLastDoc };
}
